import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../common/ui';
import ConfirmDialog from '../common/ConfirmDialog';
import ProformaDialog from './ProformaDialog';
import ProformaService from '../../services/ProformaService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtDate, fmtMoney } from '../../utils/format';

/**
 * Pro-forma invoices (pro-fakture) of the job: free positions and prices, with or without PDV, independent of
 * the job's price and payments. Admins only, behind the same password as the price (`money` from useMoneyLock).
 */
export default function ProformasCard({ job, money }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { visible, show } = money;
    const [list, setList] = useState([]);
    const [dialog, setDialog] = useState(null);     // { proforma } or {} for a new one
    const [toDelete, setToDelete] = useState(null);

    const load = useCallback(() => {
        if (!visible) { setList([]); return; }
        ProformaService.list(job.id).then(setList).catch(() => {});
    }, [job.id, visible]);
    useEffect(() => { load(); }, [load]);

    const print = (p) => navigate(`/jobs/${job.id}/proformas/${p.id}/print`);

    const save = async (payload) => {
        try {
            const saved = dialog.proforma
                ? await ProformaService.update(job.id, dialog.proforma.id, payload)
                : await ProformaService.create(job.id, payload);
            showToast(dialog.proforma ? t('proforma.updated') : t('proforma.created', { number: saved.number }), 'success');
            setDialog(null);
            load();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            throw err;
        }
    };

    const remove = async () => {
        try {
            await ProformaService.remove(job.id, toDelete.id);
            showToast(t('proforma.deleted'), 'success');
            load();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setToDelete(null);
        }
    };

    if (!visible) {
        return (
            <SectionCard title={t('proforma.title')} sx={{ mb: 2.5 }}>
                <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LockOutlinedIcon sx={{ color: '#CBD5E1' }} />
                    <Typography sx={{ flex: 1, fontSize: 13, color: '#64748B' }}>{t('proforma.hiddenText')}</Typography>
                    <Button size="small" variant="outlined" onClick={show}>{t('money.show')}</Button>
                </Box>
            </SectionCard>
        );
    }

    return (
        <SectionCard title={t('proforma.title')} sx={{ mb: 2.5 }}
                     action={<Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({})}>{t('proforma.new')}</Button>}>
            <Box sx={{ px: 2.5, py: 1 }}>
                {list.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#94A3B8', py: 1 }}>{t('proforma.none')}</Typography>
                ) : list.map(p => (
                    <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid #F8FAFC', '&:last-child': { borderBottom: 0 } }}>
                        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setDialog({ proforma: p })}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{p.number}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                                {fmtDate(p.issueDate)} · {fmtMoney(p.total)} · {p.vatEnabled ? t('vat.inclVatShort') : t('vat.noVat')}
                            </Typography>
                        </Box>
                        <Tooltip title={t('print.print')}>
                            <IconButton size="small" onClick={() => print(p)} aria-label={t('print.print')}><PrintOutlinedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit')}>
                            <IconButton size="small" onClick={() => setDialog({ proforma: p })} aria-label={t('common.edit')}><EditOutlinedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                            <IconButton size="small" onClick={() => setToDelete(p)} aria-label={t('common.delete')}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
            <ProformaDialog open={!!dialog} proforma={dialog?.proforma} job={job} onClose={() => setDialog(null)} onSave={save} />
            <ConfirmDialog open={!!toDelete} title={t('proforma.deleteTitle')}
                           text={t('proforma.deleteText', { number: toDelete?.number })}
                           onConfirm={remove} onClose={() => setToDelete(null)} />
        </SectionCard>
    );
}
